import { useEffect, useRef } from 'react';
import { integrityAPI } from '@/lib/api';

/**
 * Heartbeat-based reading session tracker.
 *
 * Fires a beat every `intervalMs` ONLY WHEN:
 *   - the document is visible (tab focused, phone not locked), AND
 *   - the user has interacted (mouse/key/touch/scroll) within `idleMs`
 *
 * On backend, gaps > idle cutoff don't count toward active_seconds. This gives
 * us honest "active reading time" vs wall-clock time and fixes the WPM skew
 * seen when students leave the story open and walk away.
 */
export default function useReadingHeartbeat({
  studentId,
  narrativeId,
  chapterNumber,
  enabled = true,
  intervalMs = 15000,
  idleMs = 25000,
}) {
  const sessionIdRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const activeSecondsRef = useRef(0);

  useEffect(() => {
    if (!enabled || !studentId || !narrativeId || !chapterNumber) return;

    // Reset on chapter change
    sessionIdRef.current = null;
    lastActivityRef.current = Date.now();

    const markActive = () => { lastActivityRef.current = Date.now(); };
    const events = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const shouldBeat = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
      if (Date.now() - lastActivityRef.current > idleMs) return false;
      return true;
    };

    const sendBeat = async () => {
      if (!shouldBeat()) return;
      try {
        const res = await integrityAPI.heartbeat({
          student_id: studentId,
          narrative_id: narrativeId,
          chapter_number: chapterNumber,
          session_id: sessionIdRef.current,
        });
        if (res?.data?.session_id) {
          sessionIdRef.current = res.data.session_id;
          activeSecondsRef.current = res.data.active_seconds || 0;
        }
      } catch {
        // Silent — reading continues even if telemetry fails
      }
    };

    // Send first beat immediately (starts the session), then on interval.
    sendBeat();
    const timer = setInterval(sendBeat, intervalMs);

    const handleVisibility = () => {
      // When tab becomes visible again, reset activity so the next beat counts.
      if (document.visibilityState === 'visible') markActive();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener('visibilitychange', handleVisibility);
      // Best-effort close
      if (sessionIdRef.current) {
        integrityAPI.closeSession({ session_id: sessionIdRef.current }).catch(() => {});
      }
    };
  }, [enabled, studentId, narrativeId, chapterNumber, intervalMs, idleMs]);

  return {
    getSessionId: () => sessionIdRef.current,
    getActiveSeconds: () => activeSecondsRef.current,
  };
}
